import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { ArrowUp, Plus } from "lucide-react";
import {
  AppHeaderBar,
  PanelTogglePlaceholder,
} from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import {
  peekSkeletonComposerText,
  saveSkeletonComposerText,
} from "./newSessionSkeletonState";
import {
  NEW_SESSION_MASCOT_SIZE_CLASS,
  NEW_SESSION_SCROLL_AREA_CLASS,
  NEW_SESSION_WELCOME_COLUMN_CLASS,
} from "./newSessionLayout";
import {
  PROMPT_PILL_ROW_CLASS,
  PromptPillSkeleton,
  LEAD_STARTER_PILL_LAYOUT_CLASS,
  STARTER_PILL_ROW_CLASS,
  StarterPromptPillSkeleton,
} from "./promptPills";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import { assetUrl } from "@/lib/assetUrl";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { cn } from "@repo/blacksmith-ui/cn";
import {
  CENTERED_LOADING_ANVIL_SIZE_CLASS,
  CENTERED_LOADING_ANVIL_SIZE_PX,
} from "@/components/agent-session/centeredLoadingAnvil";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionLoadingScreen    – centered animated anvil for existing sessions.
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

/** Loading state for an existing session. */
export function SessionLoadingScreen() {
  return (
    <div
      role="status"
      aria-label="Loading session"
      className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-app-background)]"
    >
      <span
        aria-hidden
        className={cn(
          CENTERED_LOADING_ANVIL_SIZE_CLASS,
          "flex items-center justify-center",
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={assetUrl("anvil-animations/anvil-ruminating-light.png")}
          alt=""
          width={CENTERED_LOADING_ANVIL_SIZE_PX}
          height={CENTERED_LOADING_ANVIL_SIZE_PX}
          draggable={false}
          className="select-none object-contain [image-rendering:pixelated] dark:hidden"
        />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={assetUrl("anvil-animations/anvil-ruminating-dark.png")}
          alt=""
          width={CENTERED_LOADING_ANVIL_SIZE_PX}
          height={CENTERED_LOADING_ANVIL_SIZE_PX}
          draggable={false}
          className="hidden select-none object-contain [image-rendering:pixelated] dark:block"
        />
      </span>
    </div>
  );
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into TaskStarterCards instead, so only `pr_preview` reserves
 * the PrActionCards skeleton shape (four action pills).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading.
 */
export function NewSessionChatSkeleton() {
  const isHydrated = useIsHydrated();
  // A PR-preview deep link renders PrActionCards instead of starter pills,
  // so reserve that shape here.
  const isPrAttached =
    isHydrated && hasPrPreviewInSearch(window.location.search);
  const seededText = isHydrated ? peekSkeletonComposerText() ?? "" : "";

  return (
    <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">
      <AppHeaderBar inset="canvas" className="bg-app-background">
        {isHydrated && !readLeftSidebarSkeletonLayout().visible && (
          <PanelTogglePlaceholder />
        )}
      </AppHeaderBar>

      <div className={NEW_SESSION_SCROLL_AREA_CLASS}>
        <div
          className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}
        >
          <div className="flex flex-col items-center">
            <div className="relative mt-1">
              {/* Theme swap via CSS: the skeleton can't read the resolved
                  next-themes value without risking a hydration mismatch. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={assetUrl("anvil-hello-light.png")}
                alt="[code]smith"
                className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden`}
              />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={assetUrl("anvil-hello-dark.png")}
                alt="[code]smith"
                className={`hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block`}
              />
            </div>
            {/* Greeting: real h1 is type-page-title-l (25px line height) */}
            <div className="flex h-[25px] items-center">
              <Skeleton className="h-5 w-72 max-w-full rounded" />
            </div>
          </div>

          <div className="flex w-full flex-col gap-3">
            {/* Composer: a replica of AgentChatInput's resting single-line
                state (frame, attach button, disabled send button) with a live
                textarea, so typing can start before the real composer mounts
                and adopts the text. The flavor suffix the real placeholder
                appends is randomized per mount, so only the constant prefix
                is shown here. */}
            <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]">
              <div className="relative flex min-h-8 items-center">
                <textarea
                  // The outgoing skeleton keeps taking keystrokes (or
                  // deletions) until this one commits, so apply any handoff
                  // change since render. A remount also focuses with the
                  // caret at the start; move it to the end so typing continues.
                  ref={(el) => {
                    if (!el) return;
                    const handoff = peekSkeletonComposerText() ?? "";
                    if (isHydrated && handoff !== seededText) {
                      el.value = handoff;
                    }
                    el.setSelectionRange(el.value.length, el.value.length);
                  }}
                  rows={1}
                  autoFocus
                  autoComplete="off"
                  data-1p-ignore
                  spellCheck={false}
                  aria-label="Chat input"
                  placeholder="Got a task or question?"
                  // Seeded from the handoff so keystrokes survive the stacked
                  // fallback remounts (auth check, dynamic chunk, suspense).
                  // Empty during hydration, matching the server HTML.
                  defaultValue={seededText}
                  onChange={(e) => saveSkeletonComposerText(e.target.value)}
                  className="type-chat-prose pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70"
                />
                <div className="absolute inset-y-0 left-0 flex items-center gap-1">
                  <span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary">
                    <Plus className="size-[18px]" strokeWidth={2} aria-hidden />
                  </span>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-black/[0.08] bg-gradient-to-r from-[var(--brand-gradient-blue-tertiary)] to-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500">
                    <ArrowUp
                      className="size-3.5"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  </span>
                </div>
              </div>
            </div>

            {/* Recommendations: mirrors NewSessionView so the pills don't
                jump down on resolve. The starter area matches whichever
                surface will resolve: on a PR-attached session,
                PrActionCards' four pills; otherwise TaskStarterCards'
                first paint. */}
            <div className="flex w-full flex-col gap-4">
              <div
                className={
                  isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS
                }
              >
                {isPrAttached ? (
                  Array.from({ length: PR_ACTION_PILL_COUNT }, (_, i) => (
                    <PromptPillSkeleton key={i} className="w-32" />
                  ))
                ) : (
                  <>
                    <StarterPromptPillSkeleton
                      className={LEAD_STARTER_PILL_LAYOUT_CLASS}
                    />
                    <StarterPromptPillSkeleton className="grow" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 */
function isNewSessionPath(path: string): boolean {
  const segments = path.split(/[?#]/)[0].split("/").filter(Boolean);
  return (
    segments[1] === "sessions" &&
    (segments.length === 2 || segments[2] === "new")
  );
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton() {
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const isHydrated = useIsHydrated();
  // On a hard load the pre-hydration asPath can be the route pattern
  // (`/[org]/sessions/[[...sessionPath]]`), which fails the new-session check
  // and keeps the server-rendered chat variant; the real pathname takes over
  // on the first client render. Client-side navigations always carry the
  // real path.
  const path = isHydrated ? window.location.pathname : router.asPath;

  return (
    <div className="pointer-events-none flex h-full">
      <SidebarSkeleton showSearch={surface !== "extension"} />
      {isNewSessionPath(path) ? (
        <NewSessionChatSkeleton />
      ) : (
        <SessionLoadingScreen />
      )}
    </div>
  );
}
